import {z} from 'zod'

import {BlockHighlightVisualizationEnum, CreateOrUpdateBlockHighlightDocument} from '@sdox/api'

import {EditorFeature} from '../../../../inputs/TipTap/types'
import {highlightSchema} from '../../utils/schemas'
import {updateMetaSchema} from '../../utils/getUpdateMetaFromFieldValues'
import {createFormConfig} from '../../utils/createFormConfig'
import {buttonFormToInput, buttonFragmentToForm} from '../../utils/buttonMappers'
import {MediaLibraryFileType} from '../../../../MediaLibrary/types'

import type {BlockButtonInput, BlockHighlightInput, CreateOrUpdateBlockHighlightMutation, XcNwBlock_BlockHighlightType_Fragment} from '@sdox/api'

type UpdateHighlightValidatorSchema = z.infer<typeof updateHighlightValidatorSchema>

const updateHighlightValidatorSchema = z.object({
  data: highlightSchema,
  meta: updateMetaSchema,
})

export const highlightConfig = createFormConfig<
  UpdateHighlightValidatorSchema,
  CreateOrUpdateBlockHighlightMutation,
  BlockHighlightInput,
  XcNwBlock_BlockHighlightType_Fragment
>({
  mutationOptions: {
    mutation: CreateOrUpdateBlockHighlightDocument,
  },
  fieldMapper: ({data}) => ({
    cta: buttonFormToInput(data.cta) as BlockButtonInput,
    title: data.title,
    description: data.description,
    fileHash: data.image.fileHash,
    fileHashMobile: data.imageMobile?.fileHash,
    visualization: data.visualization,
  }),
  validatorSchema: updateHighlightValidatorSchema,
  inputFields: [
    {
      label: 'Ausrichtung',
      fieldName: 'data.visualization',
      inputType: 'select',
      options: [
        {label: 'Erst Bild, dann Text', value: BlockHighlightVisualizationEnum.Default},
        {label: 'Erst Text, dann Bild', value: BlockHighlightVisualizationEnum.ViceVersa},
      ],
      defaultValue: BlockHighlightVisualizationEnum.Default,
      required: false,
    },
    {
      label: 'Bild oder Video - Desktop',
      fieldName: 'data.image',
      inputType: 'file',
      fileTypes: [
        MediaLibraryFileType.Image,
        MediaLibraryFileType.Video,
      ],
      defaultValue: '',
      required: true,
      aspectRatio: '1/1',
    },
    {
      label: 'Bild oder Video - Mobile',
      fieldName: 'data.imageMobile',
      inputType: 'file',
      fileTypes: [
        MediaLibraryFileType.Image,
        MediaLibraryFileType.Video,
      ],
      defaultValue: '',
      required: false,
      aspectRatio: '3/2',
      description: 'Ist kein Element für Mobile ausgewählt, wird stattdessen das Desktop Element im 3:2 Format angezeigt.',
    },
    {
      label: 'Title',
      fieldName: 'data.title',
      inputType: 'richtext',
      editorConfig: {
        spanAsParagraph: true,
        maxCharCount: 60,
        features: [
          EditorFeature.nobreak,
          EditorFeature.footnote,
        ],
      },
      defaultValue: '',
      required: true,
    },
    {
      label: 'Description',
      fieldName: 'data.description',
      inputType: 'textarea',
      defaultValue: '',
      required: true,
      maxLength: 250,
    },
    {
      label: 'Button Config',
      fieldName: 'divider',
      inputType: 'sectionDivider',
      defaultValue: '',
    },
    {
      label: 'Button',
      fieldName: 'data.cta',
      inputType: 'button',
      defaultValue: '',
      required: true,
    },
  ],
  getDefaultValues: (block) => ({
    data: {
      cta: buttonFragmentToForm(block.cta),
      title: block.highlightTitle,
      description: block.highlightDescription,
      image: block.media,
      imageMobile: block.mediaMobile,
      visualization: block.highlightVisualization,
    },
    meta: {
      pullout: block.pullout,
      countryCodes: block.countryCodes,
    },
  }),
})
